// src/utils/shopGeometry.js
//
// Shop shape data model + helpers. Supports two shape types:
//   - 'rect': simple rectangle defined by w/h (the original model)
//   - 'free': polygon defined by vertices + walls (new)
//
// Everything in this file is pure (no React, no DOM) so it's easy to test
// and reuse in CadCanvas, SetupPage, PresentationBuilder, etc.

// ---------- Shape constructors ----------

// Build a default rectangle shop. Matches the old { w, h, unit } shape
// but adds the new fields so downstream code can use one consistent model.
export function makeRectShop(w = 60, h = 40, unit = 'ft') {
  return {
    shapeType: 'rect',
    w,                                  // kept for backwards compatibility
    h,
    unit,
    vertices: rectToVertices(w, h),     // derived; always present so consumers can read it
    walls: rectToWalls(),               // derived; 4 walls indexed N/E/S/W
  };
}

// Build a free-form (polygon) shop from an array of vertices.
// Note: the bbox dimensions are rounded to 1 decimal because angled walls
// with whole-foot lengths land at irrational coordinates (e.g. 14.142...),
// and we don't want those long decimals showing up in the UI everywhere.
// Rounding to 0.1 ft is well below visual or practical accuracy.
export function makeFreeShop(vertices, unit = 'ft') {
  const verts = vertices.slice();
  const bbox = computeBbox(verts);
  return {
    shapeType: 'free',
    w: Math.round(bbox.w * 10) / 10,
    h: Math.round(bbox.h * 10) / 10,
    unit,
    vertices: verts,
    walls: verticesToWalls(verts),
  };
}

// ---------- Rectangle <-> polygon conversion ----------

// Convert a rectangle's w/h into a 4-vertex polygon, going clockwise from NW.
export function rectToVertices(w, h) {
  return [
    { x: 0, y: 0 },     // NW
    { x: w, y: 0 },     // NE
    { x: w, y: h },     // SE
    { x: 0, y: h },     // SW
  ];
}

// The 4 walls of a rectangle, with N/E/S/W direction tags.
// Going clockwise from NW: top wall = N, right = E, bottom = S, left = W.
//
// IMPORTANT: walls run clockwise. So:
//   N wall: starts at NW, ends at NE   (start→end runs left→right)
//   E wall: starts at NE, ends at SE   (start→end runs top→bottom)
//   S wall: starts at SE, ends at SW   (start→end runs RIGHT→LEFT)
//   W wall: starts at SW, ends at NW   (start→end runs BOTTOM→TOP)
//
// This matters for door migration — see migrateDoor() below.
export function rectToWalls() {
  return [
    { id: 0, startVertex: 0, endVertex: 1, direction: 'N' },
    { id: 1, startVertex: 1, endVertex: 2, direction: 'E' },
    { id: 2, startVertex: 2, endVertex: 3, direction: 'S' },
    { id: 3, startVertex: 3, endVertex: 0, direction: 'W' },
  ];
}

// Build walls list from any vertex array.
export function verticesToWalls(vertices) {
  return vertices.map((_, i) => ({
    id: i,
    startVertex: i,
    endVertex: (i + 1) % vertices.length,
    direction: null,
  }));
}

// ---------- Bounding box ----------

export function computeBbox(vertices) {
  if (!vertices || vertices.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, w: 0, h: 0 };
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of vertices) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.x > maxX) maxX = v.x;
    if (v.y > maxY) maxY = v.y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

// ---------- Point-in-polygon ----------

// Standard ray-casting algorithm.
export function pointInPolygon(p, vertices) {
  let inside = false;
  const n = vertices.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const vi = vertices[i], vj = vertices[j];
    const intersects = ((vi.y > p.y) !== (vj.y > p.y)) &&
      (p.x < (vj.x - vi.x) * (p.y - vi.y) / (vj.y - vi.y) + vi.x);
    if (intersects) inside = !inside;
  }
  return inside;
}

// ---------- Polygon area + perimeter ----------

export function polygonArea(vertices) {
  const n = vertices.length;
  if (n < 3) return 0;
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y - vertices[j].x * vertices[i].y;
  }
  return Math.abs(area / 2);
}

// Find the midpoint of vertices at each extreme (topmost, bottommost, etc.)
// of the polygon. Used to position cardinal compass labels naturally over
// where the polygon actually sits, instead of bbox center which can be off
// to one side for irregular shapes.
//
// Returns { topX, bottomX, leftY, rightY } where:
//   topX    = average X of vertices touching the bbox's top edge
//   bottomX = average X of vertices touching the bbox's bottom edge
//   leftY   = average Y of vertices touching the bbox's left edge
//   rightY  = average Y of vertices touching the bbox's right edge
//
// "Touching" uses a tiny epsilon so floating-point comparisons work.
// For rectangles all four extremes have 2 vertices each → midpoint = bbox center.
// For irregular shapes the extreme is wherever the polygon actually reaches.
export function polygonExtremes(vertices) {
  if (!vertices || vertices.length < 3) {
    const bb = computeBbox(vertices);
    return {
      topX: bb.minX + bb.w / 2,
      bottomX: bb.minX + bb.w / 2,
      leftY: bb.minY + bb.h / 2,
      rightY: bb.minY + bb.h / 2,
    };
  }
  const bb = computeBbox(vertices);
  const eps = 0.01;
  const topVerts = vertices.filter(v => Math.abs(v.y - bb.minY) < eps);
  const bottomVerts = vertices.filter(v => Math.abs(v.y - bb.maxY) < eps);
  const leftVerts = vertices.filter(v => Math.abs(v.x - bb.minX) < eps);
  const rightVerts = vertices.filter(v => Math.abs(v.x - bb.maxX) < eps);
  const avg = (arr, key) => arr.length === 0
    ? (bb[key === 'x' ? 'minX' : 'minY'] + bb[key === 'x' ? 'w' : 'h'] / 2)
    : arr.reduce((s, v) => s + v[key], 0) / arr.length;
  return {
    topX: avg(topVerts, 'x'),
    bottomX: avg(bottomVerts, 'x'),
    leftY: avg(leftVerts, 'y'),
    rightY: avg(rightVerts, 'y'),
  };
}

export function polygonPerimeter(vertices) {
  const n = vertices.length;
  if (n < 2) return 0;
  let p = 0;
  for (let i = 0; i < n; i++) {
    const a = vertices[i], b = vertices[(i + 1) % n];
    p += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return p;
}

// Geometric centroid of a polygon (uses the standard area-weighted formula).
// Used to position cardinal compass labels naturally on irregular shapes.
// For a rectangle this returns the bbox center; for irregular polygons it
// returns the actual visual center which feels more natural for label placement.
// Falls back to bbox center if the polygon has degenerate area.
export function polygonCentroid(vertices) {
  const n = vertices.length;
  if (n < 3) {
    const bbox = computeBbox(vertices);
    return { x: bbox.minX + bbox.w / 2, y: bbox.minY + bbox.h / 2 };
  }
  let cx = 0, cy = 0, areaSum = 0;
  for (let i = 0; i < n; i++) {
    const a = vertices[i], b = vertices[(i + 1) % n];
    const cross = a.x * b.y - b.x * a.y;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
    areaSum += cross;
  }
  if (Math.abs(areaSum) < 1e-9) {
    // Degenerate (zero-area) polygon — fall back to bbox center
    const bbox = computeBbox(vertices);
    return { x: bbox.minX + bbox.w / 2, y: bbox.minY + bbox.h / 2 };
  }
  const factor = 1 / (3 * areaSum);
  return { x: cx * factor, y: cy * factor };
}

// ---------- Self-intersection check ----------

export function isSelfIntersecting(vertices) {
  const n = vertices.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = vertices[i], b = vertices[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const c = vertices[j], d = vertices[(j + 1) % n];
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

function segmentsIntersect(a, b, c, d) {
  const ccw = (p1, p2, p3) =>
    (p3.y - p1.y) * (p2.x - p1.x) > (p2.y - p1.y) * (p3.x - p1.x);
  return ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d);
}

// ---------- Wall geometry helpers (NEW in step 4) ----------

// Return the start/end positions of a wall given a wallId.
// Used by door rendering and door drag math.
export function getWallEndpoints(shop, wallId) {
  if (!shop.walls || !shop.vertices) return null;
  const wall = shop.walls.find(w => w.id === wallId);
  if (!wall) return null;
  const a = shop.vertices[wall.startVertex];
  const b = shop.vertices[wall.endVertex];
  if (!a || !b) return null;
  return { a, b, length: Math.hypot(b.x - a.x, b.y - a.y) };
}

// Project a point onto a wall, returning the offset (distance from wall start).
// Used during door drag — given cursor position, where along the wall does it land?
// The offset is NOT clamped here — caller decides whether to clamp.
export function projectPointOntoWall(p, wallA, wallB) {
  const dx = wallB.x - wallA.x;
  const dy = wallB.y - wallA.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { offset: 0, length: 0 };
  // Dot product of (p - wallA) and the wall vector, normalized by |wall|²
  const t = ((p.x - wallA.x) * dx + (p.y - wallA.y) * dy) / len2;
  const length = Math.sqrt(len2);
  return { offset: t * length, length };
}

// Compute the rendered endpoints of a door on its wall, returning points
// in shop coordinates. Caller multiplies by scale + adds canvas origin.
// Returns null if the wall doesn't exist or has zero length.
//
// IMPORTANT: outwardVec is the direction that points AWAY from the polygon
// interior at this wall. For clockwise polygons it's -perpVec; for
// counterclockwise it's +perpVec. We detect winding by sampling a tiny step
// in the perpVec direction and checking if that point is inside the polygon.
export function getDoorEndpoints(door, shop) {
  const ends = getWallEndpoints(shop, door.wallId);
  if (!ends || ends.length === 0) return null;
  const { a, b, length } = ends;
  const ux = (b.x - a.x) / length;
  const uy = (b.y - a.y) / length;
  // Clamp so the door doesn't extend past the wall in either direction
  const startOffset = Math.max(0, Math.min(length - door.width, door.offset));
  const midpoint = {
    x: a.x + ux * (startOffset + door.width / 2),
    y: a.y + uy * (startOffset + door.width / 2)
  };
  // Standard perpendicular (rotated 90° CCW from wall direction)
  const perpVec = { x: -uy, y: ux };
  // Determine outward direction by testing a point slightly in perpVec direction
  // from the midpoint. If that test point is inside the polygon, perpVec points
  // inward, so outward is the negation. Otherwise perpVec is already outward.
  // We use a small epsilon (0.5 ft) so the test point is reliably on one side
  // even for thin walls or near-corner positions.
  let outwardVec = { x: -perpVec.x, y: -perpVec.y }; // default: -perpVec is outward (clockwise polygon)
  if (shop.vertices && shop.vertices.length >= 3) {
    const testPoint = {
      x: midpoint.x + perpVec.x * 0.5,
      y: midpoint.y + perpVec.y * 0.5,
    };
    if (!pointInPolygon(testPoint, shop.vertices)) {
      // perpVec already points outward (counterclockwise polygon, or special case)
      outwardVec = { x: perpVec.x, y: perpVec.y };
    }
  }
  return {
    start: { x: a.x + ux * startOffset, y: a.y + uy * startOffset },
    end: { x: a.x + ux * (startOffset + door.width), y: a.y + uy * (startOffset + door.width) },
    midpoint,
    unitVec: { x: ux, y: uy },
    perpVec, // raw perpendicular (rotated 90° CCW from wall direction)
    outwardVec, // points AWAY from polygon interior — use this for label/handle placement
    wallLength: length,
  };
}

// ---------- Migration ----------

// Idempotent shop migrator. See file header for the full data model.
export function migrateShop(shop) {
  if (!shop) return makeRectShop();
  if (shop.shapeType === 'rect') {
    return makeRectShop(shop.w, shop.h, shop.unit || 'ft');
  }
  if (shop.shapeType === 'free' && Array.isArray(shop.vertices) && shop.vertices.length >= 3) {
    return makeFreeShop(shop.vertices, shop.unit || 'ft');
  }
  if (typeof shop.w === 'number' && typeof shop.h === 'number') {
    return makeRectShop(shop.w, shop.h, shop.unit || 'ft');
  }
  return makeRectShop();
}

// ---------- Door migration ----------

// Convert a legacy door { wall: 'N'|..., pos: 0..1, type, width } to the
// new wall-attached format { wallId, offset, width, type }.
//
// CAREFUL: rectangle walls run clockwise, so S and W walls have their
// start-vertex on the OPPOSITE end from where the old `pos` was measured:
//   N wall: pos measured from west, wall runs west→east       → straight conversion
//   E wall: pos measured from north, wall runs north→south    → straight conversion
//   S wall: pos measured from west, wall runs EAST→WEST       → flip pos
//   W wall: pos measured from north, wall runs SOUTH→NORTH    → flip pos
//
// Idempotent: already-migrated doors pass through unchanged.
export function migrateDoor(oldDoor, shop) {
  if (typeof oldDoor.wallId === 'number' && typeof oldDoor.offset === 'number') {
    return oldDoor;
  }
  if (!shop.walls) return oldDoor;
  const wall = shop.walls.find(w => w.direction === oldDoor.wall);
  if (!wall) return oldDoor;

  const wallLengthFt = (oldDoor.wall === 'N' || oldDoor.wall === 'S') ? shop.w : shop.h;
  const doorWidthFt = oldDoor.type === 'overhead' ? (oldDoor.width || 10) : 4;
  const flipForWindingDirection = oldDoor.wall === 'S' || oldDoor.wall === 'W';
  const effectivePos = flipForWindingDirection ? (1 - oldDoor.pos) : oldDoor.pos;
  const offset = (effectivePos * wallLengthFt) - (doorWidthFt / 2);

  return {
    id: oldDoor.id,
    wallId: wall.id,
    offset: Math.max(0, Math.min(wallLengthFt - doorWidthFt, offset)),
    width: doorWidthFt,
    type: oldDoor.type,
  };
}

// Bulk-migrate an array of doors. Used at app load + project import time.
// Drops doors that can't be migrated (e.g. wall no longer exists).
export function migrateDoors(doors, shop) {
  if (!Array.isArray(doors)) return [];
  return doors
    .map(d => migrateDoor(d, shop))
    .filter(d => typeof d.wallId === 'number' && typeof d.offset === 'number');
}

// ---------- Door creation helper ----------

// Breathing room (ft) we try to keep between adjacent openings, and between an
// opening and the wall corners. Relaxed to 0 if the wall is too crowded.
const DOOR_GAP_FT = 1;

// Standard opening widths by type, in feet.
const DOOR_WIDTH_FT = { overhead: 10, door: 4 };

const buildDoor = (wallId, offset, width, type) => ({
  id: Date.now() + Math.random(),
  wallId,
  offset,
  width,
  type,
});

// Find where on the wall a new door of `width` should sit — the WIDEST free
// gap that can take it — or null if none can.
//
// `occupied` is a list of [start, end] spans in feet from the wall start,
// sorted by start. `margin` is the clearance demanded at each end of a gap.
//
// Placement within the chosen gap matters more than it looks. Centring the
// door in the gap splits the leftover space in two, so a wall fragments after
// a couple of doors and starts refusing openings it could physically hold — a
// 60ft wall would take only three 10ft overhead doors before giving up, and
// four bay doors on a 60ft wall is an ordinary shop. Packing the door against
// the neighbour that bounds the gap keeps the remaining space contiguous, so
// that same wall takes four overheads and a walk door. Bare wall corners are
// not treated as neighbours: the first door on a wall centres, which is the
// sensible default when there is nothing to line up against.
function largestGapOffset(occupied, wallLen, width, margin) {
  // Nothing on the wall yet — centre it.
  if (!occupied.length) {
    const size = wallLen - 2 * margin;
    return size >= width ? round1(margin + (size - width) / 2) : null;
  }

  let best = null;
  let bestSize = 0;
  let cursor = 0;

  // The sentinel span at the far end closes out the final gap, so the loop
  // handles "after the last door" without a special case.
  for (const [start, end] of [...occupied, [wallLen, wallLen]]) {
    const gapStart = cursor + margin;
    const gapEnd = start - margin;
    const gapSize = gapEnd - gapStart;

    if (gapSize >= width && gapSize > bestSize) {
      bestSize = gapSize;
      // cursor > 0 means a real door bounds this gap on the left;
      // start < wallLen means one bounds it on the right.
      if (cursor > 0) best = gapStart;
      else if (start < wallLen) best = gapEnd - width;
      else best = gapStart + (gapSize - width) / 2;
    }
    // max() rather than plain assignment, so pre-existing overlapping doors
    // (from an older save) can't walk the cursor backwards.
    cursor = Math.max(cursor, end);
  }

  return best === null ? null : round1(best);
}

// Round off float noise; 0.1ft is well below anything that renders.
const round1 = n => Math.round(n * 10) / 10;

// Create a new door on a given wall, placed so it does NOT overlap the doors
// already on that wall.
//
// This used to always return the wall midpoint, which meant every door added to
// the same wall stacked on top of the previous one — four doors on one wall
// rendered as a single clump. We now drop the new door into the largest free
// gap instead.
//
// `existingDoors` is the whole doors array; it's filtered to this wall here so
// callers can just hand over whatever they're already holding.
export function makeDoorOnWall(shop, wallId, type, existingDoors = []) {
  const ends = getWallEndpoints(shop, wallId);
  if (!ends) return null;

  const width = DOOR_WIDTH_FT[type] ?? DOOR_WIDTH_FT.door;
  const wallLen = ends.length;

  // Wall is physically too short for this opening — refuse, rather than hang a
  // door off the end of the building. Caller decides what to tell the user.
  if (wallLen < width) return null;

  // Spans already taken on this wall. The Number.isFinite guards keep a legacy
  // or half-migrated door (missing offset/width) from poisoning the math with
  // NaN, which would silently swallow every gap.
  const occupied = existingDoors
    .filter(
      d =>
        d.wallId === wallId &&
        Number.isFinite(d.offset) &&
        Number.isFinite(d.width)
    )
    .map(d => [d.offset, d.offset + d.width])
    .sort((a, b) => a[0] - b[0]);

  // First try honouring the gap; if the wall is too tight, allow doors flush.
  for (const margin of [DOOR_GAP_FT, 0]) {
    const offset = largestGapOffset(occupied, wallLen, width, margin);
    if (offset !== null) return buildDoor(wallId, offset, width, type);
  }

  // No gap on this wall can take the door. Note this can happen even when the
  // door widths still sum to less than the wall — the remaining space is just
  // fragmented across several gaps that are each too small.
  //
  // Return null rather than stacking it at the midpoint: silently creating an
  // overlapping door is the exact bug this function exists to prevent. Callers
  // surface a "no room" message instead.
  return null;
}
